import React, { useEffect, useState } from 'react'
import { getSpinWheelRewards, updateSpinWheelReward } from '../../../../../../API/api-endpoint'
import ToastUtils, { ErrorToastUtils } from '../../../../../../utils/ToastUtils'

const SpinWheelReward = () => {
  const [maleRewards, setMaleRewards] = useState<any[]>([])
  const [femaleRewards, setFemaleRewards] = useState<any[]>([])
  const [maleConfigId, setMaleConfigId] = useState<number | null>(null)
  const [femaleConfigId, setFemaleConfigId] = useState<number | null>(null)
  const [maleCost, setMaleCost] = useState<number>(0)
  const [femaleCost, setFemaleCost] = useState<number>(0)
  const [loading, setLoading] = useState(false)
  const [lastMaleRewards, setLastMaleRewards] = useState<any[]>([])
  const [lastFemaleRewards, setLastFemaleRewards] = useState<any[]>([])
  const [lastMaleCost, setLastMaleCost] = useState<number>(0)
  const [lastFemaleCost, setLastFemaleCost] = useState<number>(0)

  useEffect(() => {
    fetchRewards()
  }, [])

  const fetchRewards = async () => {
    setLoading(true)
    try {
      const result = await getSpinWheelRewards()
      if (result && result.male && result.female) {
        const parseConfig = (config: any) => {
          const vals = typeof config.values === 'string' ? JSON.parse(config.values) : config.values
          if (Array.isArray(vals)) return { slots: vals, cost: 0 }
          return { slots: vals.slots || [], cost: vals.costPerSpin || 0 }
        }

        const maleData = parseConfig(result.male)
        const femaleData = parseConfig(result.female)

        setMaleRewards(maleData.slots)
        setFemaleRewards(femaleData.slots)
        setMaleCost(maleData.cost)
        setFemaleCost(femaleData.cost)

        setLastMaleRewards(JSON.parse(JSON.stringify(maleData.slots)))
        setLastFemaleRewards(JSON.parse(JSON.stringify(femaleData.slots)))
        setLastMaleCost(maleData.cost)
        setLastFemaleCost(femaleData.cost)

        setMaleConfigId(result.male.id)
        setFemaleConfigId(result.female.id)
      }
    } catch (error) {
      console.error('Error fetching spin rewards:', error)
      ErrorToastUtils('Failed to load spin rewards')
    }
    setLoading(false)
  }

  const handleUpdate = async (gender: 'male' | 'female') => {
    const configId = gender === 'male' ? maleConfigId : femaleConfigId
    const values = gender === 'male' ? maleRewards : femaleRewards
    const cost = gender === 'male' ? maleCost : femaleCost
    const lastValues = gender === 'male' ? lastMaleRewards : lastFemaleRewards
    const lastCost = gender === 'male' ? lastMaleCost : lastFemaleCost

    if (!configId) return

    // Validation: Total probability must be 100%
    const totalProb = values.reduce((sum, r) => sum + (parseFloat(r.probability) || 0), 0)
    if (totalProb !== 100) {
      ErrorToastUtils(`Total probability for ${gender} must be exactly 100%. Current: ${totalProb}%`)
      return
    }

    // Only update if there are actual changes
    if (JSON.stringify(values) === JSON.stringify(lastValues) && cost === lastCost) {
      return
    }

    const result = await updateSpinWheelReward({
      configurationId: configId,
      values: {
        slots: values,
        costPerSpin: cost
      },
    })

    if (result.success) {
      if (gender === 'male') {
        setLastMaleRewards(JSON.parse(JSON.stringify(values)))
        setLastMaleCost(cost)
      } else {
        setLastFemaleRewards(JSON.parse(JSON.stringify(values)))
        setLastFemaleCost(cost)
      }
      
      ToastUtils({ type: 'success', message: `${gender.charAt(0).toUpperCase() + gender.slice(1)} rewards updated successfully` })
    } else {
      ErrorToastUtils(result.message)
    }
  }

  const handleValueChange = (gender: 'male' | 'female', index: number, field: string, value: any) => {
    if (gender === 'male') {
      const newRewards = [...maleRewards]
      newRewards[index][field] = (field === 'rewardValue' || field === 'paidRewardValue' || field === 'probability') ? parseFloat(value) || 0 : value
      setMaleRewards(newRewards)
    } else {
      const newRewards = [...femaleRewards]
      newRewards[index][field] = (field === 'rewardValue' || field === 'paidRewardValue' || field === 'probability') ? parseFloat(value) || 0 : value
      setFemaleRewards(newRewards)
    }
  }


  const renderTable = (gender: 'male' | 'female', rewards: any[]) => {
    const rewardTypes = gender === 'male' 
      ? ['credit', 'premium', 'spotlight'] 
      : ['diamond', 'premium', 'spotlight']
    
    const cost = gender === 'male' ? maleCost : femaleCost
    const setCost = gender === 'male' ? setMaleCost : setFemaleCost
    const currency = gender === 'male' ? 'Credits' : 'Diamonds'

    return (
      <div className="card mb-5 mb-xl-8">
        <div className="card-header border-0 pt-5">
          <h3 className="card-title align-items-start flex-column w-100">
            <span className="card-label fw-bold fs-3 mb-1">{gender.toUpperCase()} Spin Rewards</span>
            <div className="d-flex align-items-center mt-3 w-100 bg-light-primary p-3 rounded justify-content-between">
              <div className="d-flex align-items-center">
                <span className="text-gray-600 fw-bold me-3 fs-7">Cost per Extra Spin ({currency}):</span>
                <input
                  type="number"
                  className="form-control form-control-solid form-control-sm w-100px"
                  value={cost}
                  onChange={(e) => setCost(parseInt(e.target.value) || 0)}
                  onBlur={() => handleUpdate(gender)}
                />
              </div>
              <div className="d-flex align-items-center">
                <span className={`fw-bold fs-7 ${rewards.reduce((sum, r) => sum + (parseFloat(r.probability) || 0), 0) === 100 ? 'text-success' : 'text-danger'}`}>
                  Total Prob: {rewards.reduce((sum, r) => sum + (parseFloat(r.probability) || 0), 0)}% / 100%
                </span>
              </div>
            </div>
          </h3>
          <div className="card-toolbar">
          </div>
        </div>
        <div className="card-body py-3">
          <div className="table-responsive">
            <table className="table align-middle gs-0 gy-4">
              <thead>
                <tr className="fw-bold text-muted bg-light">
                  <th className="ps-4 min-w-50px rounded-start">No</th>
                  <th className="min-w-150px">Reward Type</th>
                  <th className="min-w-100px">Value (Free)</th>
                  <th className="min-w-100px">Value (Paid)</th>
                  <th className="min-w-100px rounded-end">Probability (%)</th>
                </tr>
              </thead>
              <tbody>
                {rewards.map((reward, index) => (
                  <tr key={index}>
                    <td>
                      <span className="text-dark fw-bold d-block fs-6 ps-4">{reward.partNumber}</span>
                    </td>
                    <td>
                      <select
                        className="form-select form-select-solid"
                        value={reward.rewardType}
                        onChange={(e) => {
                          handleValueChange(gender, index, 'rewardType', e.target.value)
                          // Trigger update immediately on type change
                          setTimeout(() => handleUpdate(gender), 0)
                        }}
                      >
                        {rewardTypes.map(type => (
                          <option key={type} value={type}>{type.charAt(0).toUpperCase() + type.slice(1)}</option>
                        ))}
                      </select>
                    </td>
                    <td>
                      <input
                        type="number"
                        className="form-control form-control-solid"
                        value={reward.rewardValue}
                        onChange={(e) => handleValueChange(gender, index, 'rewardValue', e.target.value)}
                        onBlur={() => handleUpdate(gender)}
                      />
                    </td>
                    <td>
                      <input
                        type="number"
                        className="form-control form-control-solid"
                        value={reward.paidRewardValue || 0}
                        onChange={(e) => handleValueChange(gender, index, 'paidRewardValue', e.target.value)}
                        onBlur={() => handleUpdate(gender)}
                      />
                    </td>
                    <td>
                      <input
                        type="number"
                        step="0.01"
                        className="form-control form-control-solid"
                        value={reward.probability}
                        onChange={(e) => handleValueChange(gender, index, 'probability', e.target.value)}
                        onBlur={() => handleUpdate(gender)}
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    )
  }

  if (loading) return <div>Loading Spin Rewards...</div>

  return (
    <div className="row g-5 g-xl-8">
      <div className="col-xl-6">
        {renderTable('male', maleRewards)}
      </div>
      <div className="col-xl-6">
        {renderTable('female', femaleRewards)}
      </div>
    </div>
  )
}

export default SpinWheelReward
